/**
 * Enterprise-grade strongly typed IndexedDB Client.
 * Eliminates large external library footprints while providing rich async transaction mechanics.
 */

export interface SyncQueueItem<T = any> {
  id: string;
  endpoint: string;
  method: 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  payload: T;
  timestamp: string;
  retries: number;
  status: 'pending' | 'syncing' | 'failed';
  error?: string;
  meta?: Record<string, any>; // Includes tracking info for target items like 'user_id', 'ecoponto_id'
}

export interface CachedQueryItem {
  queryKey: string;
  data: any;
  timestamp: number;
  expiresAt: number;
}

export interface TelemetryLogItem {
  id: string;
  machineId: string;
  metricName: string;
  value: number;
  timestamp: string;
}

type StoreNames = 'cached_queries' | 'sync_queue' | 'telemetry_logs';

class IndexedDBStore {
  private dbName = 'wastemap_offline_db';
  private dbVersion = 2;
  private db: IDBDatabase | null = null;

  /**
   * Initializes or returns the open connection to the IndexedDB database.
   */
  private async getDB(): Promise<IDBDatabase> {
    if (this.db) return this.db;

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
        const db = request.result;

        // Store for offline query data
        if (!db.objectStoreNames.contains('cached_queries')) {
          db.createObjectStore('cached_queries', { keyPath: 'queryKey' });
        }

        // Store for pending write operations
        if (!db.objectStoreNames.contains('sync_queue')) {
          db.createObjectStore('sync_queue', { keyPath: 'id' });
        }

        // Store for telemetry data logs (e.g. WasteMachine state, filling metrics)
        if (!db.objectStoreNames.contains('telemetry_logs')) {
          db.createObjectStore('telemetry_logs', { keyPath: 'id' });
        }
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve(request.result);
      };

      request.onerror = () => {
        reject(request.error);
      };
    });
  }

  /**
   * Generic write interface
   */
  public async set(storeName: StoreNames, value: any): Promise<void> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      const req = store.put(value);

      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  }

  /**
   * Generic retrieval interface
   */
  public async get<T = any>(storeName: StoreNames, key: string): Promise<T | null> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, 'readonly');
      const store = tx.objectStore(storeName);
      const req = store.get(key);

      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => reject(req.error);
    });
  }

  /**
   * Get all elements from an object store
   */
  public async getAll<T = any>(storeName: StoreNames): Promise<T[]> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, 'readonly');
      const store = tx.objectStore(storeName);
      const req = store.getAll();

      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
  }

  /**
   * Generic deletion interface
   */
  public async delete(storeName: StoreNames, key: string): Promise<void> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      const req = store.delete(key);

      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  }

  /**
   * Clear an entire object store
   */
  public async clearStore(storeName: StoreNames): Promise<void> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      const req = store.clear();

      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  }
}

export const indexedDBStore = new IndexedDBStore();
export default indexedDBStore;
